import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, buildQueryString } from '@/lib/api'

// ─── Shape contract — matches Slice 2's API response shape ───────────────────
// Mirrors packages/shared/src/schema/vulnerabilities.ts (+ the V1 additions
// landed in PR #272) and the admin/public response projections defined in
// the Vulns V1 spec:
//   docs/superpowers/specs/2026-04-19-vulns-v1-admin-crud-design.md
//
// Slice 2 backend lives at apps/api/src/routes/admin-vulnerabilities.routes.ts
// and is mounted at /api/admin/vulnerabilities (see apps/api/src/routes/index.ts).

export type VulnerabilityExploitationStatus =
  | 'none'
  | 'poc'
  | 'active'
  | 'widespread'

export type VulnerabilitySeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'

export interface AdminVulnerability {
  id: string
  cveId: string
  title: string
  description: string | null
  severity: VulnerabilitySeverity | null
  cvssScore: string | null
  cvssVector: string | null
  epssScore: string | null
  exploitationStatus: VulnerabilityExploitationStatus
  patchAvailable: boolean | null
  patchUrl: string | null
  adminNotes: string | null
  exploitationStatusChangedAt: string | null
  exploitationStatusChangedBy: string | null
  patchAvailableChangedAt: string | null
  patchAvailableChangedBy: string | null
  createdByEmail: string | null
  updatedByEmail: string | null
  suppressedByEmail: string | null
  suppressedAt: string | null
  firstSeen: string | null
  lastUpdated: string | null
  // V2 — CISA-KEV lifecycle tracking (additive: optional so pre-migration rows render).
  kevFirstSeenAt?: string | null
  kevLastSeenAt?: string | null
  kevRemovedAt?: string | null
  previouslyKevFlagged?: boolean | null
  // V3 — Patch classifier fields. `patchClassifiedAt` is classifier-only and
  // never edited directly from the form; `fixVersionHint` + `primaryAdvisoryUrl`
  // can be admin-overridden via the PATCH endpoint.
  fixVersionHint?: string | null
  primaryAdvisoryUrl?: string | null
  patchClassifiedAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminVulnerabilityListResponse {
  items: AdminVulnerability[]
  total: number
}

export type AdminVulnSortKey =
  | 'updated_at'
  | 'kev_removed_at'
  | 'cvss_score'
  | 'severity'

export interface AdminVulnerabilityFilters {
  search?: string
  severity?: string
  exploitation_status?: string
  patch?: 'all' | 'patched' | 'unpatched'
  needs_review?: boolean
  // V2 — CISA-KEV de-escalation filters (camelCase here, serialised to
  // the API's `recently_deescalated` snake_case key in buildFilterQuery).
  recentlyDeescalated?: boolean
  sort?: AdminVulnSortKey
  direction?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export type UpdateVulnerabilityBody = Partial<
  Pick<
    AdminVulnerability,
    | 'exploitationStatus'
    | 'patchAvailable'
    | 'adminNotes'
    | 'fixVersionHint'
    | 'primaryAdvisoryUrl'
  >
>

export interface BulkPatchVulnerabilitiesBody {
  ids: string[]
  fields: UpdateVulnerabilityBody
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const adminVulnerabilitiesKeys = {
  all: ['admin-vulnerabilities'] as const,
  list: (filters: AdminVulnerabilityFilters) =>
    [...adminVulnerabilitiesKeys.all, 'list', filters] as const,
  detail: (id: string) =>
    [...adminVulnerabilitiesKeys.all, 'detail', id] as const,
}

// Maps the hook's mixed-case filter shape to the API's snake_case query
// contract (see apps/api/src/schemas/vulnerabilities.ts → ListVulnerabilitiesQuery).
function buildFilterQuery(filters: AdminVulnerabilityFilters): string {
  const { recentlyDeescalated, ...rest } = filters
  return buildQueryString({
    ...rest,
    recently_deescalated: recentlyDeescalated,
  })
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** GET /api/admin/vulnerabilities */
export function useAdminVulnerabilities(filters: AdminVulnerabilityFilters) {
  return useQuery<AdminVulnerabilityListResponse>({
    queryKey: adminVulnerabilitiesKeys.list(filters),
    queryFn: () =>
      api.get<AdminVulnerabilityListResponse>(
        `/admin/vulnerabilities${buildFilterQuery(filters)}`,
      ),
    staleTime: 10_000,
  })
}

/** GET /api/admin/vulnerabilities/:id */
export function useAdminVulnerability(id: string | undefined) {
  return useQuery<AdminVulnerability>({
    queryKey: id
      ? adminVulnerabilitiesKeys.detail(id)
      : [...adminVulnerabilitiesKeys.all, 'detail', 'none'],
    queryFn: () => api.get<AdminVulnerability>(`/admin/vulnerabilities/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  })
}

/** PATCH /api/admin/vulnerabilities/:id */
export function useUpdateVulnerability(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateVulnerabilityBody) =>
      api.patch<AdminVulnerability>(`/admin/vulnerabilities/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVulnerabilitiesKeys.all })
      qc.invalidateQueries({ queryKey: ['vulnerabilities'] })
    },
  })
}

/** POST /api/admin/vulnerabilities/:id/accept-automated */
export function useAcceptAutomatedField(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      field: 'exploitationStatus' | 'patchAvailable'
    }) =>
      api.post<AdminVulnerability>(
        `/admin/vulnerabilities/${id}/accept-automated`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVulnerabilitiesKeys.all })
    },
  })
}

/** POST /api/admin/vulnerabilities/bulk-patch */
export function useBulkPatchVulnerabilities() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BulkPatchVulnerabilitiesBody) =>
      api.post<{ updated: number; ids: string[] }>(
        `/admin/vulnerabilities/bulk-patch`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVulnerabilitiesKeys.all })
    },
  })
}

// Re-export convenience alias matching plan naming.
export type { AdminVulnerability as Vulnerability }

// Feature-flag helper (accepts `true` or `"true"` — Vite stringifies env).
export function isVulnsAdminEnabled(): boolean {
  const v = (import.meta as unknown as { env: Record<string, unknown> }).env
    .VITE_VULNS_ADMIN_ENABLED
  return v === true || v === 'true'
}
